import { Box, Flex, TagLabel } from "@chakra-ui/react";
import { useField } from "formik";
import React from "react";
import {
  WEGLOT_APPLY_CLASS,
  WEGLOT_SKIP_CLASS,
} from "src/plugins/weglot/constants";
import { MultiSelectInput } from "../MultiSelectInput";
import { BaseInputProps, Option } from "./Question";

export interface MultiSelectProps extends BaseInputProps {
  options: Option[];
}

type MultiSelectOption = Omit<Option, "key"> & { id: string };

const toMultiSelectOption = (option: Option) => {
  const { key, ...rest } = option;
  return {
    ...rest,
    id: key,
  };
};

export const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  options,
  isDisabled,
}) => {
  const [field, meta, helpers] = useField<string[]>(id);

  const allOptions = options.map(toMultiSelectOption);
  const initialValues = allOptions.filter((o) => field.value?.includes(o.id));

  // Only non-archived options for selection in dropdown
  const nonArchivedOptions = React.useMemo(
    () => options.filter((o) => !o.isArchived).map(toMultiSelectOption),
    [options]
  );

  const renderTagContent = React.useCallback((item: MultiSelectOption) => {
    const label = item.isArchived ? `${item.label} (archived)` : item.label;
    return (
      <TagLabel
        className={item.translate ? WEGLOT_APPLY_CLASS : WEGLOT_SKIP_CLASS}
        fontStyle={item.isArchived ? "italic" : "normal"}
      >
        {label}
      </TagLabel>
    );
  }, []);

  const onChangeHandler = (values: MultiSelectOption[]) => {
    // wrapping this on setTimeout to avoid weird bug where this trigger max update stack on react component.
    setTimeout(() => {
      helpers.setValue(
        values.map((o) => o.id),
        true
      );
      helpers.setTouched(true, false);
    }, 0);
  };

  return (
    <Flex
      direction="column"
      onBlur={() => {
        helpers.setTouched(true);
      }}
    >
      <MultiSelectInput<MultiSelectOption>
        size="md"
        options={nonArchivedOptions}
        initialValues={initialValues}
        onChange={onChangeHandler}
        isDisabled={isDisabled}
        error={meta.touched && meta.error ? meta.error : undefined}
        renderDropdownItem={(item) => {
          return (
            <Box
              className={
                item.translate ? WEGLOT_APPLY_CLASS : WEGLOT_SKIP_CLASS
              }
            >
              {item.label}
            </Box>
          );
        }}
        renderTagContent={renderTagContent}
      />
    </Flex>
  );
};
